import { parseISO } from 'date-fns';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconInfoCircle } from '#src/atoms/icons/system/iconInfoCircle.ts';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { BodyS, HeadlineS, LabelM } from '#src/atoms/typography/index.tsx';
import { IntlDate } from '#src/components/IntlDate/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { useIsNotMajor } from '#src/components/fiatWallet/CreateFiatWallet/useIsNotMajor.ts';
import { GraphqlForm, TextField } from '#src/components/form/Form/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import { useScreenSize } from '#src/hooks/device/useScreenSize.ts';
import { useCreateOrUpdateFiatWallet } from '#src/hooks/wallets/useCreateOrUpdateFiatWallet.ts';
import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';
import { glossary } from '#src/lib/glossary.ts';
import { tabletAndAbove } from '#src/style/mediaQuery.ts';

const messages = defineMessages({
  title: {
    defaultMessage: 'Update my information',
  },
  description: {
    defaultMessage:
      'We’ll need to verify your new email adress <b>{email}</b> in order to change it',
  },
});

const StyledGraphqlForm = styled(GraphqlForm)`
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
  gap: var(--double-unit);
  padding: var(--double-unit);
  @media ${tabletAndAbove} {
    align-items: stretch;
  }
`;

const Helper = styled(Horizontal).attrs({ gap: 1.5 })`
  border-radius: var(--unit);
  background-color: var(--c-nd-200);
  padding: var(--intermediate-unit);
`;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const UpdateNameDialog = ({ open, onClose }: Props) => {
  const { update } = useCreateOrUpdateFiatWallet();
  const { showNotification } = useSnackNotificationContext();

  const { canDepositAndWithdraw } = useFiatBalance();
  const { up: isTablet } = useScreenSize('tablet');
  const { fiatWalletAccountable } = useCurrentUserContext();
  const { firstName, lastName, dob } = fiatWalletAccountable || {};
  // Derived per render (not captured once) so reopening the dialog after an
  // update reseeds the picker with the new date. parseISO gives local midnight,
  // which IntlDate needs to display the stored day in every timezone.
  const initialDob = dob ? parseISO(dob) : undefined;
  const [updatedFirstName, setUpdatedFirstName] = useState<string>(firstName!);
  const [updatedLastName, setUpdatedLastName] = useState<string>(lastName!);
  const [updatedDob, setUpdatedDob] = useState<Date | undefined>();
  const isNotMajor = useIsNotMajor(updatedDob);
  const { formatMessage } = useIntlContext();

  if (!firstName || !lastName) return null;

  const dobHasChanged =
    !!updatedDob && dob !== updatedDob.toISOString().slice(0, 10);
  const infoHasChanged =
    firstName !== updatedFirstName ||
    lastName !== updatedLastName ||
    dobHasChanged;
  const canSubmit =
    !!updatedFirstName && !!updatedLastName && !isNotMajor && infoHasChanged;

  const updateInfo = async (onResult: (result: any) => void) => {
    if (canSubmit) {
      const res = await update({
        firstName: updatedFirstName,
        lastName: updatedLastName,
        dob: updatedDob,
      });
      onResult(res);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={onClose}
      title={
        <HeadlineS as="h5" className="text-center">
          <FormattedMessage {...messages.title} />
        </HeadlineS>
      }
      fullScreen={!isTablet}
    >
      <StyledGraphqlForm
        onSubmit={(_, onResult) => {
          updateInfo(onResult);
        }}
        onSuccess={() => {
          showNotification(
            canDepositAndWithdraw
              ? 'cashWalletNamesUpdatedAskingKycAgain'
              : 'cashWalletNamesUpdatedDefault'
          );
          onClose();
        }}
        render={(Error, SubmitButton) => (
          <>
            {!!canDepositAndWithdraw && (
              <Helper>
                <FontAwesomeIcon icon={iconInfoCircle} />
                <BodyS color="var(--c-nd-600)">
                  <FormattedMessage defaultMessage="You will be required to upload an ID matching this change." />
                </BodyS>
              </Helper>
            )}
            <TextField
              name="firstName"
              defaultValue={firstName}
              label={formatMessage(glossary.firstName)}
              onChange={e => setUpdatedFirstName(e.target.value)}
            />
            <TextField
              name="lastName"
              defaultValue={lastName}
              label={formatMessage(glossary.lastName)}
              onChange={e => setUpdatedLastName(e.target.value)}
            />
            <BodyS color="var(--c-nd-600)">
              {formatMessage(glossary.dateOfBirth)}
            </BodyS>
            <IntlDate
              value={initialDob}
              onChange={setUpdatedDob}
              autoComplete="bday"
            />
            {!!isNotMajor && (
              <LabelM color="var(--c-red-400)">
                <FormattedMessage {...glossary.notMajorError} />
              </LabelM>
            )}
            <Error code />
            <SubmitButton disabled={!canSubmit} size="large">
              <FormattedMessage {...glossary.confirm} />
            </SubmitButton>
          </>
        )}
      />
    </Dialog>
  );
};
